var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { action, observable, toJS } from "mobx";
import { EventsBySportIdRequest } from "sb-bff-integration";
import { deepMerge } from "sb-helpers";
import { FilterPredicate, EventsRequestType, ActiveRequestsType, RecursiveRequestFunctions } from "../typings";
import Debounce from "debounce-decorator";
import { bffRequestBuilder, queryBuilder, DateRangeMapper, LoadedData, isDifferentResponse, uniqueElementsById, isEntityFromType, compareArrays, FilterManager } from "../helpers";
const minLoadDelay = 2000;
export class Retriever {
    constructor(bff, sdapi, dataProvider, cardConfig, filterManager, deviceChangeDispatcherMock) {
        this.FullLoadEventsCount = 100;
        this.ReloadTimeout = 60000;
        this.InvalidSportId = "-1";
        /**
         * Execute according request for fetching data on every x milliseconds - ReloadTimeout
         */
        this.loadData = () => {
            if (this.isRequestAvailable()) {
                window.clearTimeout(this.reloadTimeout);
                this.sub && this.sub.unsubscribe();
                Object.keys(this.activeRequests).forEach(type => {
                    this[`loadData${this.activeRequests[type].type}`](type);
                    5;
                });
                this.reloadTimeout = window.setTimeout(() => this.loadData(), this.ReloadTimeout);
            }
        };
        this.loadDataProxy = () => {
            const currTime = new Date().getTime();
            const timeDiff = currTime - this.lastLoadTime;
            if (this.minLoadDelay > timeDiff) {
                setTimeout(this.loadData, this.minLoadDelay - timeDiff);
                return;
            }
            this.lastLoadTime = currTime;
            this.loadData();
        };
        this.bff = bff;
        this.sdapi = sdapi;
        this.dataProvider = dataProvider;
        this.activeRequests = this.initializeRequests();
        this.deviceChangeDispatcher = deviceChangeDispatcherMock;
        this.data = { events: observable([]), markets: observable([]), leagues: observable([]) };
        this.temporaryData = { events: [], markets: [], leagues: [] };
        this.loadedEvents = 0;
        this.reloadTimeout = -1;
        this.subLoaders = this.initializeLoaders();
        this.filterManager = filterManager;
        this.regularUpdate = this.regularUpdate.bind(this);
        this.regularUpdateTimeOut = this.regularUpdateTimeOut.bind(this);
        this.processError = this.processError.bind(this);
        this.applyAddref = this.applyAdd.bind(this);
        this.sub = undefined;
        this.changesCache = { events: [], markets: [] };
        this.addsCache = { events: [], markets: [] };
        this.loadedData = new LoadedData();
        this.currentSports = [];
        this.isMiniEventCard = this.dataProvider.isMiniEventCard;
        this.cardConfig = cardConfig;
        this.lastLoadTime = 0;
        this.minLoadDelay = minLoadDelay;
    }
    activate() {
        Object.keys(this.activeRequests).forEach(type => {
            this.setLoader(true, type);
        });
        window.clearTimeout(this.reloadTimeout);
        this.clearData();
        this.deviceChangeDispatcher && this.deviceChangeDispatcher.activate();
    }
    deactivate() {
        Object.keys(this.activeRequests).forEach(type => {
            this.setLoader(false, type);
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFirstLoad = true;
        });
        window.clearTimeout(this.reloadTimeout);
        this.clearData();
        this.currentSports = [];
        this.sub && this.sub.unsubscribe();
        this.deviceChangeDispatcher && this.deviceChangeDispatcher.deactivate();
        this.filterManager.setMainPredicate(this.dataProvider.filterPredicate);
        this.lastLoadTime = 0;
    }
    setParams(sportIds) {
        Object.keys(this.activeRequests).forEach(type => {
            bffRequestBuilder.buildIds(this.activeRequests[type].request, sportIds);
        });
        this.currentSports = sportIds;
    }
    /**
     * Update query filter to be for specific sport ids
     * @param {string[]} sportIds Newly selected sport ids
     */
    querySportUpdate(sportIds) {
        this.currentSports = sportIds;
        if (this.currentSports.length === 1 && this.currentSports[0] === this.InvalidSportId) {
            this.finishLoading();
        }
        let shouldUpdateFirstLoad = this.loadedData.updateSports(sportIds);
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            shouldUpdateFirstLoad && (this.subLoaders[type].isFirstLoad = true);
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type);
            let bffRequestParams = this.getBFFRequestParams(type);
            bffRequestBuilder.buildSportIdsUpdate(bffRequestParams, sportIds);
        });
        this.filterManager.setMainPredicate(FilterPredicate.Sport);
        this.loadDataProxy();
    }
    /**
     * Update query filter to be for specific date range
     * @param {DateRangeProp} dateRange Newly selected date range
     */
    queryDatesUpdate(dateRange, sportIds) {
        let shouldUpdateFirstLoad = this.loadedData.updateDates(sportIds, dateRange.from || new Date());
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            shouldUpdateFirstLoad && (this.subLoaders[type].isFirstLoad = true);
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type);
            let bffRequestParams = this.getBFFRequestParams(type);
            bffRequestBuilder.buildDateRangeUpdate(bffRequestParams, dateRange);
        });
        this.filterManager.setMainPredicate(FilterPredicate.Date);
        this.loadDataProxy();
    }
    /**
     * Update query filter to be for specific league ids from sport ids
     * @param {string[]} leagueIds Newly selected league ids
     * @param {string[]} sportIds Newly selected sport ids
     */
    queryLeaguesUpdate(leagueIds, sportIds) {
        this.currentSports = sportIds;
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            this.subLoaders[type].isFirstLoad = true;
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type);
            let bffRequestParams = this.getBFFRequestParams(type);
            bffRequestBuilder.buildLeagueIdsUpdate(bffRequestParams, leagueIds, sportIds);
        });
        this.filterManager.setMainPredicate(FilterPredicate.Sport);
        this.loadDataProxy();
    }
    /**
     *
     * @param {string[]} regionId
     */
    queryRegionIdUpdate(regionId, sportId) {
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            this.subLoaders[type].isFirstLoad = true;
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type);
            let bffRequestParams = this.getBFFRequestParams(type);
            var pagination = this.recalculatePagination({}, false);
            bffRequestBuilder.buildRegionIdUpdate(bffRequestParams, regionId, sportId, pagination);
        });
        this.filterManager.setMainPredicate(FilterPredicate.Sport);
        this.loadDataProxy();
    }
    /**
     * Update query filter to be for current tag
     * @param {string} currentTag Tag to be changed
     */
    queryTagsUpdate(currentTag) {
        let shouldUpdateFirstLoad = this.loadedData.updateTags(currentTag);
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            shouldUpdateFirstLoad && (this.subLoaders[type].isFirstLoad = true);
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type);
            let bffRequestParams = this.getBFFRequestParams(type);
            bffRequestBuilder.buildEventTagsUpdate(bffRequestParams, [currentTag]);
        });
        this.filterManager.setMainPredicate(FilterPredicate[currentTag.toString()]);
        this.loadDataProxy();
    }
    /**
     * Update query filter to be for favourites
     * @param {string[]} eventIds Ids of favourite events
     */
    queryFavoritesUpdate(favoriteEvents) {
        this.currentSports = favoriteEvents.map(e => e.sportId);
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            this.subLoaders[type].isFirstLoad = true;
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type, false);
            let bffRequestParams = this.getBFFRequestParams(type);
            bffRequestBuilder.buildEventIdsUpdate(bffRequestParams, favoriteEvents);
        });
        this.filterManager.setMainPredicate(FilterPredicate.Favorites);
        this.loadDataProxy();
    }
    /**
     * Process received data and query recursively if needed
     * @param {EventsResponse} response
     * @param {Pagination} pagination
     */
    loadDataSuccessCb(response, pagination, requestType) {
        if (response.events && response.events.length) {
            this.subLoaders[requestType].isFirstLoad ? this.loadNewData(response) : this.updateTemporaryData(response);
            if (response.events.length === pagination.top && this.loadedEvents < this.dataProvider.maxEventsToLoad) {
                return this.recalculatePagination(pagination);
            }
        }
        return false;
    }
    /**
     * Calculate request pagination despite the received amount of data
     * @param {Pagination} pagination Current pagination of the request. Empty by default.
     */
    recalculatePagination(pagination, shouldRecalcSkip = true) {
        const fullLoad = Math.min(this.dataProvider.maxEventsToLoad, this.FullLoadEventsCount);
        const loadedEvents = (pagination.top || 0) + (pagination.skip || 0);
        return {
            top: Math.min(this.dataProvider.maxEventsToLoad - this.loadedEvents, fullLoad),
            skip: shouldRecalcSkip ? loadedEvents : 0
        };
    }
    /**
     * Inital received data processing
     * @param {EventsResponse} response
     */
    loadNewData(response) {
        this.add({ events: response.events, markets: response.markets });
        this.replaceLeagues({ leagues: response.leagues });
    }
    /**
    * renew Data on JWTToken change
    */
    renewJWTToken() {
        setTimeout(this.loadDataProxy, 0);
    }
    /**
    * sets the customers regionId
    * @param {regionId} customers regionId
    */
    setRegionId(regionId) {
        Object.keys(this.activeRequests).forEach(type => {
            bffRequestBuilder.buildRegionIds(this.activeRequests[type].request, this.dataProvider.ipCountry.concat(regionId));
        });
    }
    /**
    * removes the customers regionId
    *
    */
    removeRegionId() {
        Object.keys(this.activeRequests).forEach(type => {
            bffRequestBuilder.buildRegionIds(this.activeRequests[type].request, this.dataProvider.ipCountry);
        });
    }
    /**
     * Helper function for updating current data and constructing new query for SportData API (request by top leagues)
     * @param {EventsResponse} response
     */
    buildUpdateQueryByTopLeagues(response, requestType) {
        const queryParams = this.getQueryParams(requestType);
        this.query = queryBuilder.buildQueryTopLeagues(queryParams);
        this.updateData(queryParams.request, response, requestType);
    }
    /**
     * Helper function for updating current data and constructing new query for SportData API (request by ids)
     * @param {EventsResponse} response
     */
    buildUpdateQueryByRegionId(response, requestType) {
        const queryParams = this.getQueryParams(requestType);
        this.query = queryBuilder.buildQueryByRegionId(queryParams);
        this.updateData(queryParams.request, response, requestType);
    }
    /**
     * Helper function for updating current data and constructing new query for SportData API (request by ids)
     * @param {EventsResponse} response
     */
    buildUpdateQueryByEventIds(response, requestType) {
        const queryParams = this.getQueryParams(requestType);
        this.query = queryBuilder.buildQueryByEventIds(queryParams);
        this.updateData(queryParams.request, response, requestType);
    }
    /**
     * Helper function for updating current data and constructing new query for SportData API (request by ids)
     * @param {EventsResponse} response
     */
    buildUpdateQueryByLeagueIds(response, requestType) {
        const queryParams = this.getQueryParams(requestType);
        this.query = queryBuilder.buildQueryByLeagueIds(queryParams);
        this.updateData(queryParams.request, response, requestType);
    }
    /**
     * Helper function for updating current data and constructing new query for SportData API (request by ids)
     * @param {EventsResponse} response
     */
    buildUpdateQueryByPersonalization(response, requestType) {
        const queryParams = this.getQueryParams(requestType);
        const eventIds = this.getEventIds(response);
        this.query = queryBuilder.buildQueryByEventIdsOnly(queryParams, eventIds);
        this.updateData(queryParams.request, response, requestType);
    }
    /**
     * Helper function for updating current data and constructing new query for SportData API (request by ids)
     * @param {EventsResponse} response
     */
    buildUpdateQueryByCouponId(response, requestType) {
        const queryParams = this.getQueryParams(requestType);
        const eventIds = this.getEventIds(response);
        this.query = queryBuilder.buildQueryByEventIdsOnly(queryParams, eventIds);
        this.updateData(queryParams.request, response, requestType);
    }
    /**
     * Get eventIds from response
     * @param {EventsResponse} response
     */
    getEventIds(response) {
        if (!response.events) {
            return [];
        }
        return response.events.reduce((arr, ev) => {
            ev.id && arr.push(ev.id);
            return arr;
        }, []);
    }
    /**
     * Subscribe for updates from SportData API
     */
    querySub() {
        this.sub = this.sdapi.subscribeForEvents(this.query);
    }
    /**
     * Handler for update events - execute add,change or remove methods
     * @param {EventPushResponse} x
     */
    regularUpdate(x) {
        Object.keys(x.data).forEach(prop => {
            let property = prop === "add" ? "addChanges" : prop;
            this[property](x.data[prop]);
        });
    }
    /**
     * Reset observable data
     */
    clearData() {
        this.data.events.replace([]);
        this.data.markets.replace([]);
        this.data.leagues.replace([]);
        this.temporaryData = { events: [], markets: [], leagues: [] };
        this.loadedData.clearData();
    }
    // VERY IMPORTANT:
    // SDAPI is sending one event per change
    // 20 events - 20 time triggering the update logic and render
    // batching them and executing every half a second reduces
    // CPU usage to almost none
    /**
     * Handler for "Change" event from SportData API
     * @param {EventPayload} x
     */
    change(x) {
        Object.keys(this.changesCache).map(prop => this.changesCache[prop].splice(this.changesCache[prop].length, 0, ...x[prop]));
        this.applyChanges();
    }
    applyChanges() {
        this.batchedChange(this.changesCache);
        this.changesCache = { events: [], markets: [] };
    }
    replaceLeagues(x) {
        this.data.leagues.replace(uniqueElementsById(x.leagues.concat(this.data.leagues.slice())));
    }
    /**
     * Add and update currently stored data
     * @param {EventsResponse} x
     * @param {Function} filterFn Filter predicate
     */
    updateCurrentData(x, filterFn, requestType) {
        let collections = this.filterManager.getMainFilterCollections();
        Object.keys(this.data).forEach(collection => {
            const currentData = this.data[collection].slice();
            const dataToRemain = collections.indexOf(collection) > -1
                ? currentData.filter(e => !filterFn(e) || !isEntityFromType(e, requestType, this.data.events))
                : currentData;
            this.data[collection].replace(uniqueElementsById(dataToRemain.concat(x[collection])));
        });
    }
    /**
     * Handler for 'Add' update event from SportData API
     * @param {EventPayload} x
     */
    add(x) {
        Object.keys(x).forEach(prop => this.data[prop].push.apply(this.data[prop], x[prop]));
    }
    addChanges(x) {
        Object.keys(this.addsCache).forEach(prop => this.addsCache[prop].concat(x[prop]));
        if (this.addsCache.events.length > 0 || this.addsCache.markets.length > 0) {
            this.applyAddref(this.addsCache);
        }
    }
    applyAdd() {
        this.batchedAdd(this.addsCache);
        this.addsCache = { events: [], markets: [] };
    }
    batchedAdd(x) {
        let tempObject = Object.assign({}, x);
        Object.keys(this.data).forEach(prop => {
            if (x[prop]) {
                tempObject[prop] = this.data[prop].concat(x[prop].slice());
                this.data[prop].replace(tempObject[prop]);
            }
            else {
                tempObject[prop] = this.data[prop].slice();
            }
        });
    }
    /**
     * Handler for 'Remove' update event from SportData API
     * @param {EventPayload} x
     */
    remove(x) {
        Object.keys(x).forEach(prop => {
            x[prop].forEach(y => {
                let i = this.data[prop].findIndex(z => z.id === y);
                if (i > -1) {
                    this.data[prop].splice(i, 1);
                }
            });
        });
    }
    batchedChange(x) {
        Object.keys(x).forEach(prop => {
            x[prop].forEach(y => {
                let i = this.data[prop].findIndex(z => z.id === y.id);
                if (i > -1) {
                    let item = deepMerge(toJS(this.data[prop][i]), y);
                    if (prop === "markets" && y.selections && y.selections.length) {
                        item.selections = y.selections;
                    }
                    this.data[prop][i] = item;
                }
            });
        });
    }
    /**
     * Get current filter function
     * @param {EventListPresenterRequest} request
     */
    getFilterFunction(request, requestType) {
        const ids = this.getFilterIds(request, requestType);
        let from, to;
        if (request.dateRange) {
            this.dataProvider.useNewTimeOnEveryRequest && (request.dateRange.from = new Date());
            from = request.dateRange.from;
            to = DateRangeMapper[request.dateRange.timeRange](from);
        }
        return this.filterManager.filterEntity.bind(FilterManager, ids, from, to);
    }
    /**
     * Check if market is from the required type
     * @param {string} marketId
     * @param {Market} x
     */
    filterByMarketId(marketId, x) {
        return !!marketId ? x.marketType.id === marketId : true;
    }
    /**
     * Request events by top leagues and sport ids
     */
    loadDataByTopLeagues(requestType) {
        let activeRequest = Object.assign({}, this.activeRequests[requestType].request);
        let loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        // loader.isFirstLoad && this.setLoader(true, requestType);
        this.bff.getEventsFromTopLeaguesBySportId(activeRequest)
            .then(response => {
            if (this.isValidResponse(this.activeRequests[requestType].request, response, requestType)) {
                return;
            }
            this.buildUpdateQuery(activeRequest, response, requestType);
            if (response.events && response.events.length < this.dataProvider.maxEventsToLoad &&
                (response.events.length === 0 || !this.dataProvider.isTopLeaguesOnly)) {
                this.loadedEvents = response.events.length;
                const nextPagination = this.recalculatePagination({ top: response.events.length, skip: 0 }, false);
                const request = bffRequestBuilder.buildSportIdRequest(activeRequest, nextPagination);
                this.loadDataBySportId(request, requestType);
            }
            else {
                loader.isLoading = false;
                //this.setLoader(false, requestType);
                this.updateWithTemporaryData(activeRequest, requestType);
            }
        }).catch(this.processError);
    }
    /**
     * Request events by sport ids
     * @param {EventsBySportIdRequest} request Request for events from regular league ids
     */
    loadDataBySportId(request, requestType) {
        this.bff.getEventsBySportId(request)
            .then(response => {
            if (this.isValidResponse(this.activeRequests[requestType].request, response, requestType)) {
                return;
            }
            this.processRecursiveResponse(request, response, requestType, RecursiveRequestFunctions.BySportId);
        }).catch(this.processError);
    }
    updateWithTemporaryData(request, requestType) {
        if (!this.subLoaders[requestType].isFirstLoad) {
            let filterFn = this.getFilterFunction(request, requestType);
            this.temporaryData.leagues = uniqueElementsById(this.temporaryData.leagues);
            this.updateCurrentData(this.temporaryData, filterFn, requestType);
        }
        this.subLoaders[requestType].isFullLoadFinished = true;
        this.subLoaders[requestType].isFirstLoad = false;
        // this.setLoaderOnUpdate(requestType, this.subLoaders);
        if (!this.loader().isFirstLoad && this.loader().isFullLoadFinished) {
            this.temporaryData = { events: [], markets: [], leagues: [] };
        }
    }
    // private setLoaderOnUpdate(requestType: string, subLoaders: SubLoader) {
    //     this.subLoaders[requestType].isFullLoadFinished = subLoaders.fixtures.isFullLoadFinished && subLoaders.outrights.isFullLoadFinished;
    //     this.subLoaders[requestType].isFirstLoad = false;
    // }
    /**
     * Request events by event ids
     */
    loadDataByEventIds(requestType) {
        let activeRequest = this.activeRequests[requestType].request;
        let loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        //loader.isFirstLoad && this.setLoader(true, requestType);
        this.bff.getEventsByEventId(activeRequest)
            .then(response => {
            this.buildUpdateQuery(activeRequest, response, requestType);
            this.updateWithTemporaryData(activeRequest, requestType);
            this.setLoader(false, requestType);
        }).catch(this.processError);
    }
    /**
     * Request events by country
     */
    loadDataByRegionId(requestType) {
        let activeRequest = this.activeRequests[requestType].request;
        let loader = this.subLoaders[requestType];
        this.loadedEvents = 0;
        bffRequestBuilder.buildPagination(activeRequest, this.recalculatePagination({}, false));
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        //loader.isFirstLoad && this.setLoader(true, requestType);
        this.bff.getEventsByRegionId(activeRequest)
            .then(response => {
            this.buildUpdateQuery(activeRequest, response, requestType);
            this.loadedEvents = response.events ? response.events.length : 0;
            const nextPagination = this.recalculatePagination(activeRequest.pagination);
            if (nextPagination) {
                bffRequestBuilder.buildPagination(activeRequest, nextPagination);
                this.loadDataByRegionIdAndPagination(activeRequest, requestType);
            }
            else {
                loader.isLoading = false;
                // this.setLoader(false, requestType);
                this.updateWithTemporaryData(activeRequest, requestType);
            }
        }).catch(this.processError);
    }
    loadDataByRegionIdAndPagination(request, requestType) {
        this.bff.getEventsByRegionId(request)
            .then(response => {
            this.processRecursiveResponse(request, response, requestType, RecursiveRequestFunctions.ByRegionId);
        }).catch(this.processError);
    }
    processRecursiveResponse(request, response, requestType, nextRequestMethod) {
        let loader = this.subLoaders[requestType];
        this.loadedEvents += response.events ? response.events.length : 0;
        const nextPagination = this.loadDataSuccessCb(response, request.pagination || {}, requestType);
        loader.isLoading = false;
        //this.setLoader(false, requestType);
        if (nextPagination) {
            bffRequestBuilder.buildPagination(request, nextPagination);
            this[nextRequestMethod] && this[nextRequestMethod](request, requestType);
        }
        else {
            this.updateWithTemporaryData(request, requestType);
        }
    }
    /**
     * Request events by league ids
     */
    loadDataByLeagueIds(requestType) {
        let activeRequest = this.activeRequests[requestType];
        let loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        // loader.isFirstLoad && this.setLoader(true, requestType);
        this.bff.getEventsByLeagueId(activeRequest.request)
            .then(response => {
            this.buildUpdateQuery(activeRequest, response, requestType);
            this.updateWithTemporaryData(activeRequest.request, requestType);
            this.setLoader(false, requestType);
        }).catch(this.processError);
    }
    /**
     * Request events by personalization
     */
    loadDataByPersonalization(requestType) {
        const activeRequest = this.activeRequests[requestType].request;
        const loader = this.subLoaders[requestType];
        loader.isLoading = true;
        loader.isFullLoadFinished = false;
        // loader.isFirstLoad && this.setLoader(true, requestType);
        this.bff.getPersonalizedPreLiveEventsBySportId(activeRequest)
            .then(initialResponse => {
            if (this.isValidResponse(this.activeRequests[requestType].request, initialResponse, requestType)) {
                return;
            }
            const response = Object.assign(Object.assign({}, initialResponse), { leagues: [] });
            this.buildUpdateQuery(activeRequest, response, requestType);
            this.updateWithTemporaryData(activeRequest, requestType);
            this.setLoader(false, requestType);
        }).catch(this.processError);
    }
    /**
     * Request events by coupon id
     */
    loadDataByCouponId(requestType) {
        const activeRequest = this.activeRequests[requestType].request;
        const loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        // loader.isFirstLoad && this.setLoader(true, requestType);
        this.bff.getEventsByCouponId(activeRequest)
            .then(response => {
            this.buildUpdateQuery(activeRequest, response, requestType);
            this.updateWithTemporaryData(activeRequest, requestType);
            this.setLoader(false, requestType);
        }).catch(this.processError);
    }
    /**
     * Sets loader props
     * @param {boolean} isRequestInProcess Is request in process
     */
    setLoader(isRequestInProcess, type) {
        this.subLoaders[type].isLoading = isRequestInProcess;
        this.subLoaders[type].isFullLoadFinished = !isRequestInProcess;
    }
    /**
     * Process error from bff request
     */
    processError(error) {
        Object.keys(this.subLoaders).forEach(type => {
            this.setLoader(false, type);
        });
        // TODO: Process error
        console["error"](error);
    }
    /**
     * Update current data and subscribe for its updates
     * @param {EventListPresenterRequest} request Current request
     * @param {EventsResponse} response Received response
     */
    //It never receives requestType = "outrights" as we are not subscribing fot outright updates
    updateData(request, response, requestType) {
        // Get the current filtering function and pass in
        this.updateRetrieverData(request, response, requestType);
        // Need to understand how we are going to pull more than 100 items
        // Probably going to be solved on the BFF
        // sub to query only after it arrives
        // missing some of the changes that happened during the pull
        // SDAPI team is working on improving performance of the pull
        this.querySub();
    }
    updateTemporaryData(response) {
        Object.keys(this.temporaryData).forEach(collection => {
            this.temporaryData[collection] = this.temporaryData[collection].concat(response[collection]);
        });
    }
    /**
     * Set active BFF request to request by event ids
     * @param {boolean} isDefault Is default events request from data provider or not
     */
    setActiveRequest(type, isDefault = true) {
        let defaultRequest = (type === ActiveRequestsType.fixtures
            ? Object.assign({}, this.dataProvider.eventsRequest) : Object.assign({}, this.dataProvider.eventsRequestOutrights));
        this.activeRequests[type] = isDefault ? defaultRequest : {
            request: {
                eventTypes: defaultRequest && defaultRequest.request ? defaultRequest.request.eventTypes : []
            },
            type: EventsRequestType.ByEventIds
        };
    }
    /**
     * Get query params for query to SportData API
     */
    getQueryParams(type) {
        return {
            request: this.activeRequests[type].request,
            messageHandler: this.regularUpdateTimeOut,
        };
    }
    regularUpdateTimeOut(x) {
        setTimeout(this.regularUpdate.bind(this), 0, x);
    }
    /**
     * Get BFF request params for query to BFF
     */
    getBFFRequestParams(type) {
        return {
            request: this.activeRequests[type].request,
            cardConfiguration: this.cardConfig.getConfig(this.isMiniEventCard),
            allSportIds: this.dataProvider.allSportIds
        };
    }
    initializeRequests() {
        let requests = {};
        if (this.dataProvider.eventsRequest) {
            requests.fixtures = Object.assign({}, this.dataProvider.eventsRequest);
        }
        if (this.dataProvider.eventsRequestOutrights) {
            requests.outrights = Object.assign({}, this.dataProvider.eventsRequestOutrights);
        }
        return requests;
    }
    initializeLoaders() {
        let fixturesloader = this.dataProvider.eventsRequest
            ? { isLoading: true, isFirstLoad: true, isFullLoadFinished: false }
            : { isLoading: false, isFirstLoad: true, isFullLoadFinished: true };
        let outrightsloader = this.dataProvider.eventsRequestOutrights
            ? { isLoading: true, isFirstLoad: true, isFullLoadFinished: false }
            : { isLoading: false, isFirstLoad: true, isFullLoadFinished: true };
        return {
            fixtures: fixturesloader,
            outrights: outrightsloader
        };
    }
    isRequestAvailable() {
        return (this.activeRequests.fixtures && this.isIdsAvailable(this.activeRequests.fixtures.request)) ||
            (this.activeRequests.outrights && this.isIdsAvailable(this.activeRequests.outrights.request));
    }
    isIdsAvailable(request) {
        return (request.ids && request.ids.length) || request.sportId;
    }
    loader() {
        return {
            isLoading: this.subLoaders.fixtures.isLoading || this.subLoaders.outrights.isLoading,
            isFirstLoad: this.subLoaders.fixtures.isFirstLoad && this.subLoaders.outrights.isFirstLoad,
            isFullLoadFinished: this.subLoaders.fixtures.isFullLoadFinished && this.subLoaders.outrights.isFullLoadFinished
        };
    }
    buildUpdateQuery(request, response, requestType) {
        // Not subscribing for SportData API updates when outright request
        requestType === ActiveRequestsType.outrights
            ? this.updateRetrieverData(request, response, requestType)
            : this[`buildUpdateQuery${this.activeRequests[requestType].type}`](response, requestType);
        // if (requestType === ActiveRequestsType.outrights) {
        //     this.updateRetrieverData(request, response, requestType);
        //     this.setOutrightsFirstLoad(request, false);
        //     this.updateRetrieverData(request, response, requestType);
        // } else {
        //     this[`buildUpdateQuery${this.activeRequests[requestType].type}`](response, requestType);
        // }
    }
    // private setOutrightsFirstLoad(request, isFirstLoad: boolean) {
    //     request && this.updateSports(request, ActiveRequestsType.outrights) && (this.subLoaders.outrights.isFirstLoad = isFirstLoad);
    // }
    // private updateSports(request, requestType: string): boolean {
    //     return this.isIdsAvailable(request) && !this.loadedData.updateSports(this.getFilterIds(request, requestType));
    // }
    updateRetrieverData(request, response, requestType) {
        let filterFn = this.getFilterFunction(request, requestType);
        this.subLoaders[requestType].isFirstLoad ? this.updateCurrentData(response, filterFn, requestType) : this.updateTemporaryData(response);
    }
    getFilterIds(request, requestType) {
        if (this.activeRequests[requestType].type === EventsRequestType.ByLeagueIds) {
            return this.currentSports;
        }
        if (this.activeRequests[requestType].type === EventsRequestType.ByRegionId) {
            return [request.sportId];
        }
        return request.ids;
    }
    isValidResponse(activeRequest, response, requestType) {
        const isCurrentRequest = activeRequest.ids ? !compareArrays(activeRequest.ids, this.activeRequests[requestType].request.ids) : false;
        return isCurrentRequest || isDifferentResponse(activeRequest, response);
    }
    finishLoading() {
        Object.keys(this.activeRequests).forEach(type => {
            this.setLoader(false, type);
        });
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], Retriever.prototype, "data", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Retriever.prototype, "subLoaders", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], Retriever.prototype, "isMiniEventCard", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "activate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "deactivate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "querySportUpdate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Array]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "queryDatesUpdate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array, Array]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "queryLeaguesUpdate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, String]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "queryRegionIdUpdate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "queryTagsUpdate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "queryFavoritesUpdate", null);
__decorate([
    Debounce(200),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "applyChanges", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "replaceLeagues", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Function, String]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "updateCurrentData", null);
__decorate([
    Debounce(500),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "applyAdd", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "batchedAdd", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "remove", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "batchedChange", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean, String]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "setLoader", null);
//# sourceMappingURL=retriever.js.map